import React, { ReactNode } from "react";
import { Button, Container, Col, Row } from "react-bootstrap";
import { BookHalf, Funnel, FunnelFill, Sun, Moon } from "react-bootstrap-icons";
import { createExportSchedule } from "../../../utils/Requests";
import { getDefaultDateRange, getWidgetTheme } from "../../../utils/analytics";
import {
    AnalyticsFilters,
    AnalyticsFilterOptions,
    ChartOptions,
    DocsSidebarEntry,
    TimezoneOption,
    TrendlineType
} from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { DocsSidebar, ExportButton, PopPeriodBanner, ScheduleExportModal } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import ScatterplotWidget from "../widgets/ScatterplotWidget";

type ScatterplotPageProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ScatterplotExportRow = {
    brandName: string | null;
    brandId: string | null;
    xValue: number | null;
    yValue: number | null;
    sizeValue: number | null;
};

type ScatterplotPageState = {
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    filtersCollapsed: boolean;
    isDarkTheme: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    scatterRows: ScatterplotExportRow[];
    xMetric: string;
    yMetric: string;
    topN: number;
};

class Scatterplot extends React.Component<ScatterplotPageProps, ScatterplotPageState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: ScatterplotPageProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };

        this.state = {
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandNames: [],
                devicePlatforms: []
            },
            filterKey: 0,
            filtersCollapsed: false,
            isDarkTheme: true,
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            scatterRows: [],
            xMetric: "video_starts",
            yMetric: "users",
            topN: 25
        };
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }));
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private handleMetaLoaded = (entry: DocsSidebarEntry): void => {
        this.setState(prevState => ({
            sidebarEntries: [
                ...prevState.sidebarEntries.filter(current => current.widgetKey !== entry.widgetKey),
                entry
            ]
        }));
    };

    private handleScatterDataLoaded = (payload: {
        rows: ScatterplotExportRow[];
        xMetric: string;
        yMetric: string;
        topN: number;
    }): void => {
        this.setState({
            scatterRows: payload.rows,
            xMetric: payload.xMetric,
            yMetric: payload.yMetric,
            topN: payload.topN
        });
    };

    private getExportData = (): ExportableData[] => {
        const { scatterRows, xMetric, yMetric, topN, appliedFilters } = this.state;

        if (scatterRows.length === 0) {
            return [];
        }

        return [{
            title: "Scatterplot",
            columns: [
                { key: "brandName", label: "Brand" },
                { key: "brandId", label: "Brand ID" },
                { key: "xValue", label: xMetric, format: "number" },
                { key: "yValue", label: yMetric, format: "number" },
                { key: "sizeValue", label: "video_starts", format: "number" }
            ],
            rows: scatterRows,
            metadata: {
                filters: {
                    ...appliedFilters,
                    focusField: undefined,
                    focusSize: topN
                },
                pageKey: "scatterplot",
                customerSlug: this.props.customerConfig.slug
            }
        }];
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Scatterplot",
            pageKey: "scatterplot",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    public render(): ReactNode {
        const {
            filters,
            filterOptions,
            appliedFilters,
            filterKey,
            filtersCollapsed,
            isDarkTheme,
            sidebarOpen,
            sidebarEntries,
            showScheduleModal,
            scatterRows
        } = this.state;
        const theme = getWidgetTheme(isDarkTheme);

        const chartOptions: ChartOptions = {
            showTrendline: false,
            trendlineType: TrendlineType.MOVING_AVERAGE,
            focusSize: 10
        };
        const timezone: TimezoneOption = "local";

        return (
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Container ref={this.containerRef} className="mw-100" key="scatterplot" style={{ position: "relative", paddingBottom: "40px" }}>
                        <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                            <ExportButton
                                context={this.getExportContext()}
                                enabled={scatterRows.length > 0}
                                scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                                onScheduleClick={() => this.setState({ showScheduleModal: true })}
                            />
                            <Button
                                onClick={this.toggleTheme}
                                style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                            >
                                {isDarkTheme ? <Sun /> : <Moon />}
                            </Button>
                            <Button
                                onClick={() => this.setState(prevState => ({ filtersCollapsed: !prevState.filtersCollapsed }))}
                                style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                title={filtersCollapsed ? "Show filters" : "Hide filters"}
                            >
                                {filtersCollapsed ? <Funnel /> : <FunnelFill />}
                            </Button>
                            <Button
                                onClick={this.toggleSidebar}
                                style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                            >
                                <BookHalf />
                            </Button>
                        </div>

                        <Row>
                            <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                                <h2 style={{ color: theme.text }}>Scatterplot</h2>
                            </Col>
                        </Row>

                        <AnalyticsFiltersComponent
                            filters={filters}
                            filterOptions={filterOptions}
                            onFilterChange={this.handleFilterChange}
                            onApplyFilters={this.handleApplyFilters}
                            customerConfig={this.props.customerConfig}
                            enableGroupBy={{}}
                            isCollapsed={filtersCollapsed}
                        />

                        <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                        <Row style={{ marginTop: "24px" }}>
                            <Col md={12}>
                                <ScatterplotWidget
                                    key={`scatterplot-${filterKey}`}
                                    customerConfig={this.props.customerConfig}
                                    username={this.props.username}
                                    groups={this.props.groups}
                                    filters={appliedFilters}
                                    isDarkTheme={isDarkTheme}
                                    theme={theme}
                                    chartOptions={chartOptions}
                                    timezone={timezone}
                                    onMetaLoaded={this.handleMetaLoaded}
                                    onDataLoaded={this.handleScatterDataLoaded}
                                />
                            </Col>
                        </Row>

                        <ScheduleExportModal
                            show={showScheduleModal}
                            onHide={() => this.setState({ showScheduleModal: false })}
                            filters={appliedFilters}
                            pageKey="scatterplot"
                            pageTitle="Scatterplot"
                            customerSlug={this.props.customerConfig.slug}
                            editingSchedule={null}
                            onSave={this.handleScheduleSave}
                        />
                    </Container>
                </div>
                <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default Scatterplot;
